import { useContext, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import DrishtiLogin from './pages/auth/drishtiLogin';
import DrishtiCalibration from './pages/device/drishtiCalibration';
import DrishtiConfig from './pages/device/drishtiConfig';
import DrishtiGuide from './pages/device/drishtiGuide';
import DrishtiSite from './pages/device/drishtiSite';
import DrishtiStation from './pages/device/drishtiStation';
import { useAppSelector } from './app/hooks';
import { AppConfigContext } from './config/appConfigContext';
import { useDrishtiPool } from './config/drishtiPoolConfig';
import { clearDrishtiRefreshToken, refreshDrishtiSession } from './services/drishtiAuthService';
import { type DrishtiSession, isDrishtiSession, toDrishtiUserSession } from './services/drishtiSession';
import { EventName, EventType, logDrishtiMetricEvent } from './services/eventService';
import { clearDrishtiSession, updateDrishtiSession } from './slice/drishtiUserInfoSlice';

export { isDrishtiPath } from './services/drishtiSession';

export const DRISHTI_PATH = {
    LOGIN: '/drishti/login',
    SITE: '/drishti/site',
    STATION: '/drishti/station',
    CONFIG: '/drishti/config',
    GUIDE: '/drishti/guide',
    CALIBRATION: '/drishti/calibration',
} as const;

// The pool check rejects a session minted by a pool this stage no longer configures.
const useDrishtiSession = (): DrishtiSession | undefined => {
    const pool = useDrishtiPool();
    const session = useAppSelector((state) => state.drishtiUserData.session);
    return isDrishtiSession(session, pool?.userPoolId) ? session : undefined;
};

// Both guards share this, or an expired session bounces between them; the refresh hook keeps the wider test.
const useLiveDrishtiSession = (): DrishtiSession | undefined => {
    const session = useDrishtiSession();
    return session && (session.expire ?? 0) * 1000 > Date.now() ? session : undefined;
};

const RequireLogin = ({ children }: { children: ReactElement }) => {
    const session = useLiveDrishtiSession();
    return session ? children : <Navigate to={DRISHTI_PATH.LOGIN} replace={true} />;
};

const RequireNoLogin = ({ children }: { children: ReactElement }) => {
    const session = useLiveDrishtiSession();
    return session ? <Navigate to={DRISHTI_PATH.SITE} replace={true} /> : children;
};

export const DRISHTI_ROUTES = [
    {
        path: DRISHTI_PATH.LOGIN,
        element: (
            <RequireNoLogin>
                <DrishtiLogin />
            </RequireNoLogin>
        ),
    },
    {
        element: (
            <RequireLogin>
                <Outlet />
            </RequireLogin>
        ),
        children: [
            { path: DRISHTI_PATH.SITE, element: <DrishtiSite /> },
            { path: DRISHTI_PATH.STATION, element: <DrishtiStation /> },
            { path: DRISHTI_PATH.CONFIG, element: <DrishtiConfig /> },
            { path: DRISHTI_PATH.GUIDE, element: <DrishtiGuide /> },
            { path: DRISHTI_PATH.CALIBRATION, element: <DrishtiCalibration /> },
        ],
    },
    // Guarded, so an unauthenticated technician lands on login and a signed-in one on the site picker.
    { path: '*', element: <Navigate to={DRISHTI_PATH.SITE} replace={true} /> },
] satisfies RouteObject[];

const router = createBrowserRouter(DRISHTI_ROUTES);

export const CONFIG_LOAD_TIMEOUT_MS = 10000;

// Re-mint this far ahead of expiry so a technician mid-install never carries a rejected token.
export const REFRESH_LEAD_MS = 5 * 60 * 1000;

/*
 * Floor on the timer: a token already inside the lead window waits a minute rather than firing at once,
 * so a clock running ahead of Cognito re-mints once a minute instead of spinning on every fresh token.
 */
export const MIN_REFRESH_DELAY_MS = 60 * 1000;

// A failed refresh retries at this interval for the whole lead window, so a blip is not a sign-out.
export const REFRESH_RETRY_DELAY_MS = 30 * 1000;

/*
 * FleetEdge's CognitoRefreshToken resolves its session through SDK localStorage keys that only a
 * password sign-in writes, so it cannot see a Drishti session; Drishti runs this timer instead. The
 * refresh token is the service's own module state, so a caller passes only the pool and the address.
 */
const useDrishtiSessionRefresh = (): void => {
    const dispatch = useDispatch();
    const pool = useDrishtiPool();
    const drishtiSession = useDrishtiSession();
    const email = drishtiSession?.email;
    const expire = drishtiSession?.expire;

    useEffect(() => {
        if (!pool || email === undefined || expire === undefined) {
            return;
        }
        const expiresAtMs = expire * 1000;
        let timer: ReturnType<typeof setTimeout>;
        const refresh = async () => {
            try {
                const result = await refreshDrishtiSession({ ...pool, email });
                if (result.ok) {
                    // The new expire re-runs this effect, which schedules the following refresh.
                    dispatch(updateDrishtiSession(toDrishtiUserSession(result.value)));
                    logDrishtiMetricEvent({
                        eventName: EventName.DRISHTI_COGNITO_TOKEN_REFRESH,
                        eventType: EventType.SUCCESS,
                    });
                    return;
                }
            } catch {
                // A rejection is outside the service's Result contract, and fails the refresh the same way.
            }
            logDrishtiMetricEvent({ eventName: EventName.DRISHTI_COGNITO_TOKEN_REFRESH, eventType: EventType.FAILURE });
            if (Date.now() < expiresAtMs) {
                // The token still works, so a blip gets the rest of the lead window to recover.
                timer = setTimeout(() => void refresh(), REFRESH_RETRY_DELAY_MS);
                return;
            }
            // Out of runway: sign out rather than leave the technician holding a token the authorizer refuses.
            clearDrishtiRefreshToken();
            dispatch(clearDrishtiSession());
        };
        timer = setTimeout(
            () => void refresh(),
            Math.max(expiresAtMs - REFRESH_LEAD_MS - Date.now(), MIN_REFRESH_DELAY_MS),
        );
        return () => clearTimeout(timer);
    }, [pool, email, expire, dispatch]);
};

function DrishtiApp() {
    const { config }: { config: any } = useContext(AppConfigContext);
    const installationConfig = config.installation_config;
    const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');

    useEffect(() => {
        if (installationConfig) {
            setLoadState('ready');
            return;
        }
        const timer = setTimeout(() => setLoadState('error'), CONFIG_LOAD_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [installationConfig]);

    useDrishtiSessionRefresh();

    if (loadState === 'error') {
        return <div>Couldn't load configuration. Refresh to try again.</div>;
    }
    if (loadState !== 'ready') {
        return <div> Loading... </div>;
    }
    return <RouterProvider router={router} />;
}

export default DrishtiApp;
